import { computed, unref } from 'vue';
import { useHead } from '@vueuse/head';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
  SITE_NAME,
  DEFAULT_OG_IMAGE,
  TWITTER_HANDLE,
  DEFAULT_OG_IMAGE_WIDTH,
  DEFAULT_OG_IMAGE_HEIGHT,
  DEFAULT_LOCALE,
} from '@/config/seo';

import { canonicalUrl, languageLinks, INDEXABLE_LOCALES, OG_LOCALES, routeNoindex, serializeJsonLd } from '@/config/seoUrls';

/**
 * Composable that wires per-page SEO into the document head via @vueuse/head.
 *
 * Accepts an options object whose values may be plain strings, refs, or
 * computeds — everything is normalised through `unref` and recomputed when
 * dependencies (route, locale, options) change.
 *
 * @param {object} options
 * @param {string|Ref<string>} [options.title] - Final <title> (without suffix).
 * @param {string|Ref<string>} [options.titleKey] - i18n key for the title.
 * @param {string|Ref<string>} [options.description] - Meta description.
 * @param {string|Ref<string>} [options.descriptionKey] - i18n key for the description.
 * @param {string|Ref<string>} [options.ogImage] - Absolute URL for og:image.
 * @param {string|Ref<string>} [options.ogType] - og:type, defaults to "website".
 * @param {string|Ref<string>} [options.canonical] - Override canonical URL.
 * @param {boolean|Ref<boolean>} [options.noindex] - Emit noindex/follow robots tag.
 * @param {Array|Ref<Array>} [options.jsonLd] - Array of JSON-LD objects to inject.
 * @param {string|Ref<string>} [options.keywords] - Meta keywords (legacy).
 */
export const usePageSeo = (options = {}) => {
  const route = useRoute();
  const { t, locale } = useI18n();

  const head = computed(() => {
    const titleRaw = unref(options.title);
    const titleKey = unref(options.titleKey);
    const descriptionRaw = unref(options.description);
    const descriptionKey = unref(options.descriptionKey);
    const ogImage = unref(options.ogImage) || DEFAULT_OG_IMAGE;
    const ogType = unref(options.ogType) || 'website';
    const canonicalOverride = unref(options.canonical);
    const noindex = unref(options.noindex) === true || routeNoindex(route);
    const locales = unref(options.locales) || INDEXABLE_LOCALES;
    const contentLocale = unref(options.contentLocale) || (locales.includes(locale.value) ? locale.value : DEFAULT_LOCALE);
    const keywords = unref(options.keywords);
    const jsonLd = unref(options.jsonLd) || [];

    const title = titleRaw || (titleKey ? t(titleKey) : SITE_NAME);
    const description = descriptionRaw || (descriptionKey ? t(descriptionKey) : '');

    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    const canonical = canonicalUrl(canonicalOverride || route.path, contentLocale);

    const meta = [
      { name: 'description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:type', content: ogType },
      { property: 'og:url', content: canonical },
      { property: 'og:image', content: ogImage },
      { property: 'og:site_name', content: SITE_NAME },
      { property: 'og:locale', content: OG_LOCALES[contentLocale] || OG_LOCALES.en },
      { property: 'og:image:alt', content: unref(options.imageAlt) || title },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:site', content: TWITTER_HANDLE },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: description },
      { name: 'twitter:image', content: ogImage },
      { name: 'twitter:image:alt', content: unref(options.imageAlt) || title },
      { name: 'robots', content: noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large' },
    ];

    if (keywords) {
      meta.push({ name: 'keywords', content: keywords });
    }

    if (!options.fallback && ogImage === DEFAULT_OG_IMAGE) {
      meta.push({ property: 'og:image:width', content: String(DEFAULT_OG_IMAGE_WIDTH) }, { property: 'og:image:height', content: String(DEFAULT_OG_IMAGE_HEIGHT) });
    }
    for (const [property, value] of Object.entries(unref(options.articleMeta) || {})) {
      if (value) meta.push({ property: `article:${property}`, content: value });
    }

    const link = [
      { rel: 'canonical', href: canonical },
      ...languageLinks(canonicalOverride || route.path, noindex ? [] : locales),
    ];

    const script = jsonLd
      .filter(Boolean)
      .map((data) => ({
        type: 'application/ld+json',
        children: serializeJsonLd(data),
      }));

    return {
      title: fullTitle,
      htmlAttrs: { lang: unref(locale) || DEFAULT_LOCALE },
      meta,
      link,
      script,
    };
  });

  useHead(head);

  return { head };
};

export default usePageSeo;
